import { api } from './api'

// Constants
const TARGET_RATIO = 4 / 5
const BRIGHTNESS = '105%'
const CONTRAST = '110%'
const OUTPUT_FORMAT = 'image/jpeg'
const OUTPUT_QUALITY = 0.92

// Upload validation constants
export const ALLOWED_FORMATS = ['image/jpeg', 'image/png', 'image/webp']
export const MAX_FILE_SIZE_MB = 20
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024
export const MIN_DIMENSION = 800
const BLUR_THRESHOLD = 50
const UNDEREXPOSED_THRESHOLD = 50
const OVEREXPOSED_THRESHOLD = 220

/**
 * Apply brightness/contrast colour correction and 4:5 aspect ratio with white padding.
 * @param {string} base64ImageData - Raw base64 image data (no data: prefix)
 * @param {string} contentType - MIME type of the source image
 * @returns {Promise<string>} Processed base64 image data
 */
export function applyColourAndRatio(base64ImageData, contentType) {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.crossOrigin = 'anonymous'

    image.onload = () => {
      const { canvas, ctx } = createPaddedCanvas(image)
      const offsetX = Math.round((canvas.width - image.width) / 2)
      const offsetY = Math.round((canvas.height - image.height) / 2)

      ctx.filter = `brightness(${BRIGHTNESS}) contrast(${CONTRAST})`
      ctx.drawImage(image, offsetX, offsetY)

      const base64 = canvas.toDataURL(OUTPUT_FORMAT, OUTPUT_QUALITY).split(',')[1]
      resolve(base64)
    }

    image.onerror = () => reject(new Error('Failed to load image for processing'))
    image.src = `data:${contentType};base64,${base64ImageData}`
  })
}

/**
 * Remove background from an image via the remove-background API.
 * @param {string} imageUrl - Public URL of the image to process
 * @returns {Promise<{image_data: string, content_type: string}>}
 */
export async function removeBackground(imageUrl) {
  const response = await api.post('/api/image/remove-background', {
    image_url: imageUrl,
    alpha_matting: true,
    alpha_matting_foreground_threshold: 270,
    alpha_matting_background_threshold: 20,
    alpha_matting_erode_size: 15
  })

  if (!response.success || !response.image_data) {
    throw new Error('Background removal failed')
  }

  return {
    image_data: response.image_data,
    content_type: response.content_type || OUTPUT_FORMAT
  }
}

/**
 * Fetch an image URL and convert it to base64.
 * @param {string} url - Image URL to fetch
 * @returns {Promise<{base64: string, contentType: string}>}
 */
export async function urlToBase64(url) {
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`Failed to fetch image: ${response.status}`)
  }

  const blob = await response.blob()
  const contentType = blob.type || OUTPUT_FORMAT

  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const base64 = reader.result.split(',')[1]
      resolve({ base64, contentType })
    }
    reader.onerror = () => reject(new Error('Failed to convert image to base64'))
    reader.readAsDataURL(blob)
  })
}

/**
 * Validate a file before upload. Returns null if valid, or an error message string.
 * Checks format, file size, and dimensions.
 * @param {File} file
 * @returns {Promise<string|null>} Error message or null if valid
 */
export function validateImageFile(file) {
  return new Promise((resolve) => {
    if (!ALLOWED_FORMATS.includes(file.type)) {
      resolve(`"${file.name}" — unsupported format. Use JPEG, PNG, or WebP.`)
      return
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      resolve(`"${file.name}" — file too large (${Math.round(file.size / 1024 / 1024)}MB). Maximum is ${MAX_FILE_SIZE_MB}MB.`)
      return
    }

    const url = URL.createObjectURL(file)
    const image = new Image()
    image.onload = () => {
      URL.revokeObjectURL(url)
      if (image.width < MIN_DIMENSION || image.height < MIN_DIMENSION) {
        resolve(`"${file.name}" — too small (${image.width}×${image.height}). Minimum is ${MIN_DIMENSION}×${MIN_DIMENSION}px.`)
        return
      }
      resolve(null)
    }
    image.onerror = () => {
      URL.revokeObjectURL(url)
      resolve(`"${file.name}" — could not read image.`)
    }
    image.src = url
  })
}

/**
 * Run visual quality checks on a file (blur + exposure).
 * Returns null if acceptable, or an error message string.
 * @param {File} file
 * @returns {Promise<string|null>} Error message or null if acceptable
 */
export function checkImageQuality(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file)
    const image = new Image()
    image.onload = () => {
      URL.revokeObjectURL(url)

      // Downscale for analysis preserving aspect ratio
      const maxAnalysisSize = 256
      const scale = Math.min(maxAnalysisSize / image.width, maxAnalysisSize / image.height)
      const analysisWidth = Math.round(image.width * scale)
      const analysisHeight = Math.round(image.height * scale)
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      canvas.width = analysisWidth
      canvas.height = analysisHeight
      ctx.drawImage(image, 0, 0, analysisWidth, analysisHeight)
      const imageData = ctx.getImageData(0, 0, analysisWidth, analysisHeight)

      const blurScore = computeLaplacianVariance(imageData)
      if (blurScore < BLUR_THRESHOLD) {
        resolve(`"${file.name}" — photo appears blurry. Please retake with better focus.`)
        return
      }

      const avgBrightness = computeAverageBrightness(imageData)
      if (avgBrightness < UNDEREXPOSED_THRESHOLD) {
        resolve(`"${file.name}" — photo is too dark (underexposed). Please retake with better lighting.`)
        return
      }
      if (avgBrightness > OVEREXPOSED_THRESHOLD) {
        resolve(`"${file.name}" — photo is too bright (overexposed). Please retake with less light.`)
        return
      }

      resolve(null)
    }
    image.onerror = () => {
      URL.revokeObjectURL(url)
      resolve(`"${file.name}" — could not analyse image quality.`)
    }
    image.src = url
  })
}

// --- Internal helpers ---

/**
 * Create a white-filled canvas sized to 4:5 ratio, centred around the source image.
 */
function createPaddedCanvas(image) {
  const currentRatio = image.width / image.height
  let newWidth, newHeight

  if (currentRatio > TARGET_RATIO) {
    newWidth = image.width
    newHeight = Math.round(image.width / TARGET_RATIO)
  } else if (currentRatio < TARGET_RATIO) {
    newHeight = image.height
    newWidth = Math.round(image.height * TARGET_RATIO)
  } else {
    newWidth = image.width
    newHeight = image.height
  }

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  canvas.width = newWidth
  canvas.height = newHeight
  ctx.fillStyle = '#FFFFFF'
  ctx.fillRect(0, 0, newWidth, newHeight)

  return { canvas, ctx }
}

/**
 * Compute Laplacian variance as a blur metric.
 * Higher values = sharper image. Low values = blurry.
 */
function computeLaplacianVariance(imageData) {
  const { data, width, height } = imageData
  const grey = new Float32Array(width * height)

  // Convert to greyscale
  for (let i = 0; i < grey.length; i++) {
    const offset = i * 4
    grey[i] = 0.299 * data[offset] + 0.587 * data[offset + 1] + 0.114 * data[offset + 2]
  }

  // Apply 3×3 Laplacian kernel: [0,1,0; 1,-4,1; 0,1,0]
  let sum = 0
  let sumSq = 0
  let count = 0

  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const idx = y * width + x
      const laplacian =
        grey[idx - width] +
        grey[idx - 1] +
        (-4 * grey[idx]) +
        grey[idx + 1] +
        grey[idx + width]
      sum += laplacian
      sumSq += laplacian * laplacian
      count++
    }
  }

  const mean = sum / count
  return (sumSq / count) - (mean * mean)
}

/**
 * Compute average brightness (0–255) across all pixels.
 */
function computeAverageBrightness(imageData) {
  const { data } = imageData
  let total = 0
  const pixelCount = data.length / 4

  for (let i = 0; i < data.length; i += 4) {
    total += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]
  }

  return total / pixelCount
}
